import * as actions from '~/artifact/actions';
import _ from 'lodash';

const defaultState = {
  isAnalyzing: false,
  isValidating: false,
  isApplying: false,
  analyzingError: null,
  validatingError: null,
  vulnerableMethodToAnalyze: null,
  validatedMethod: null,
  activeUrl: '',
  isFetching: false,
  isSubmittingArtifact: false,
  viewMode: 'VIEW',
  activeTab: 'summary',
  activeVulnRangeTab: 'versions',
  artifact: {},
  affectedComponents: [],
  editedArtifact: {},
  edits: {},
  markdownHtml: {
    overview: '',
    teardown: '',
    fixText: '',
    exploitRunning: '',
    exploitRequirements: '',
    exploitDescription: '',
    exploitTechnicalDetails: '',
  },
  isFetchingMarkdown: {
    overview: false,
    teardown: false,
  },
  artifactComponents: [],
  isAddingComponent: false,
  isChoosingComponent: false,
  isFetchingComponentNameMatches: false,
  addingComponentName: '',
  addingComponentNameMatches: [],
  componentBeforeEdits: null,
  rangeBeforeEdits: null,
  fixLinkBeforeEdits: null,
  artifactLinkBeforeEdits: null,
  exploitBeforeEdits: null,
  vulnerableVersionsByComponent: {},
  currentlyEditing: {
    componentIndex: null,
    rangeIndex: null,
    fixLinkIndex: null,
    artifactLinkIndex: null,
    exploitId: null,
    vulnMethodComponentId: null,
    vulnMethodRangeId: null,
    newExploit: {
      type: 'EXPLOIT',
      title: 'new exploit',
      running: '',
      requirements: '',
      description: '',
      technicalDetails: '',
      exploits: [],
    },
  },
  riskBreakdownSection: 'base',
  componentId: null,
  componentVersion: null,
  activeVulnerableVersion: null,
  isDisplayVulnerableVersions: false,
  signatureSection: 'coordinates',
  componentPackageManager: null,
  vulnerableRangeByComponent: {},
  isRangeSelectFocused: false,
  fixSection: 'direct',
  fixPackageManager: null,
  exploitId: null,
  exploitSection: 'readme',
  vulnerableMethods: [],
  referenceFilters: {},
  isReferenceDropdownActive: false,
  isFetchingSafeVersionsAfterRange: false,
  isFetchingVulnerableVersions: false,
  safeVersionsAfterRange: [],
  activeSafeVersion: null,
  isFetchingExploits: false,
  frontEndValidation: {},
  currentExploitDownloadFileUrl: '',
  fixInfoByComponentAndCommitHash: {},
  commitHash: '',
  activeSafeCommitHash: '',
  exploitability: {
    epss_score: null,
    epss_percentile: null,
    exploit_observed: null,
    exploit_source: null,
    exploit_note: null
  }
};

const artifactState = (state = defaultState, action) => {
  switch (action.type) {
    case actions.UPDATE_ARTIFACT_ACTIVE_URL:
      return Object.assign({}, state, {
        activeUrl: action.url,
      });
    case actions.UPDATE_ARTIFACT_VIEW_MODE:
      return Object.assign({}, state, {
        viewMode: action.viewMode,
      });
    case actions.UPDATE_ARTIFACT_ACTIVE_TAB:
      return Object.assign({}, state, {
        activeTab: action.tab,
      });
    case actions.UPDATE_ACTIVE_VULN_RANGE_TAB:
      return Object.assign({}, state, {
        activeVulnRangeTab: action.tab,
      });
    case actions.UPDATE_ARTIFACT_ACTIVE_ARTIFACT:
      return Object.assign({}, state, {
        artifact: action.artifact,
      });
    case actions.REQUEST_COMPONENTS:
      return Object.assign({}, state, {
        isFetching: true,
      });
    case actions.RECEIVE_COMPONENTS:
      return Object.assign({}, state, {
        affectedComponents: action.affectedComponents,
        isFetching: false,
      });
    case actions.UPDATE_ARTIFACT_EDITS: {
      const { field, value } = action;
      const newEdits = Object.assign({}, state.edits);

      newEdits[field] = value;

      return Object.assign({}, state, {
        edits: newEdits,
      });
    }
    case actions.UPDATE_ARTIFACT_ACTIVE_COMMIT_HASH:
      return Object.assign({}, state, {
        commitHash: action.commitHash,
      });
    case actions.UPDATE_ACTIVE_SAFE_COMMIT_HASH:
      return Object.assign({}, state, {
        activeSafeCommitHash: action.activeSafeCommitHash,
      });
    case actions.LOAD_EDITED_ARTIFACT:
      return Object.assign({}, state, {
        editedArtifact: action.editedArtifact,
        frontEndValidation: {},
      });
    case actions.RESET_ADDING_COMPONENT:
      return Object.assign({}, state, {
        isChoosingComponent: false,
        isAddingComponent: false,
        addingComponentNameMatches: [],
        addingComponentName: '',
      });
    case actions.RECEIVE_COMPONENT_NAME_MATCHES:
      return Object.assign({}, state, {
        addingComponentNameMatches: action.addingComponentNameMatches,
        isFetchingComponentNameMatches: action.isFetchingComponentNameMatches,
        isChoosingComponent: action.isChoosingComponent,
      });
    case actions.REQUEST_COMPONENT_NAME_MATCHES:
      return Object.assign({}, state, {
        addingComponentNameMatches: action.addingComponentNameMatches,
        isFetchingComponentNameMatches: action.isFetchingComponentNameMatches,
        isChoosingComponent: action.isChoosingComponent,
      });
    case actions.UPDATE_IS_DISPLAY_VULNERABLE_VERSIONS:
      return Object.assign({}, state, {
        isDisplayVulnerableVersions: action.bool,
      });

    case actions.RECEIVE_MARKDOWN_HTML: {
      const { html: markdownHtml, field } = action;
      const newMarkdownHtml = Object.assign({}, state.markdownHtml);

      newMarkdownHtml[field] = markdownHtml;

      return Object.assign({}, state, {
        markdownHtml: newMarkdownHtml,
        isFetchingMarkdown: {
          ...state.isFetchingMarkdown,
          [field]: false,
        },
      });
    }
    case actions.REQUEST_MARKDOWN_HTML: {
      return {
        ...state,
        isFetchingMarkdown: {
          ...state.isFetchingMarkdown,
          [action.field]: true,
        },
      };
    }
    case actions.RESET_MARKDOWN_HTML: {
      const { field } = action;
      const newMarkdownHtml = Object.assign({}, state.markdownHtml);

      newMarkdownHtml[field] = '';

      return Object.assign({}, state, {
        markdownHtml: newMarkdownHtml,
        isFetchingMarkdown: {
          ...state.isFetchingMarkdown,
          [field]: false,
        },
      });
    }
    case actions.UPDATE_RISK_BREAKDOWN_SECTION:
      return Object.assign({}, state, {
        riskBreakdownSection: action.section,
      });
    case actions.UPDATE_COMPONENT_ID:
      return Object.assign({}, state, {
        componentId: action.id,
      });
    case actions.UPDATE_EDITED_ARTIFACT: {
      const { field, value } = action;
      const newEditedArtifact = _.cloneDeep(state.editedArtifact);

      newEditedArtifact[field] = value;
      return Object.assign({}, state, {
        editedArtifact: newEditedArtifact,
      });
    }
    case actions.SUBMIT_ARTIFACT_EDITS:
      return Object.assign({}, state, {
        isSubmittingArtifact: true,
      });
    case actions.RECEIVE_SUBMITTED_ARTIFACT:
      return Object.assign({}, state, {
        artifact: action.artifact,
        editedArtifact: action.artifact,
        viewMode: 'VIEW',
        isSubmittingArtifact: false,
        edits: {},
        frontEndValidation: {},
      });
    case actions.ENABLE_ADDING_COMPONENT:
      return Object.assign({}, state, {
        isAddingComponent: true,
      });
    case actions.TOGGLE_ADDING_COMPONENT: {
      const currentState = state.isAddingComponent;
      return Object.assign({}, state, {
        isAddingComponent: !currentState,
      });
    }
    case actions.DISABLE_ADDING_COMPONENT:
      return Object.assign({}, state, {
        isAddingComponent: false,
      });
    case actions.UPDATE_ARTIFACT_CURRENTLY_EDITING: {
      const newCurrentlyEditing = Object.assign(
        {},
        state.currentlyEditing,
        action.currentlyEditing
      );
      return Object.assign({}, state, {
        currentlyEditing: newCurrentlyEditing,
      });
    }
    case actions.UPDATE_ARTIFACT_CURRENTLY_VIEWING: {
      const newCurrentlyViewing = Object.assign(
        {},
        state.currentlyViewing,
        action.currentlyViewing
      );
      return Object.assign({}, state, {
        currentlyViewing: newCurrentlyViewing,
      });
    }
    case actions.UPDATE_COMPONENT_BEFORE_EDITS:
      return Object.assign({}, state, {
        componentBeforeEdits: action.component,
      });
    case actions.UPDATE_ARTIFACT_RANGE_BEFORE_EDITING:
      return Object.assign({}, state, {
        rangeBeforeEdits: action.range,
      });
    case actions.UPDATE_ARTIFACT_FIX_LINK_BEFORE_EDITING:
      return Object.assign({}, state, {
        fixLinkBeforeEdits: action.fixLink,
      });
    case actions.UPDATE_ARTIFACT_COMPONENTS:
      return Object.assign({}, state, {
        artifactComponents: action.components,
      });
    case actions.UPDATE_ARTIFACT_LINK_BEFORE_EDITING:
      return Object.assign({}, state, {
        artifactLinkBeforeEdits: action.artifactLink,
      });
    case actions.REQUEST_VULNERABLE_COMPONENT_VERSIONS: {
      return {
        ...state,
        isFetchingVulnerableVersions: true,
      };
    }
    case actions.RECEIVE_VULNERABLE_COMPONENT_VERSIONS: {
      const newVulnerableVersionsByComponent = Object.assign(
        {},
        state.vulnerableVersionsByComponent
      );

      newVulnerableVersionsByComponent[action.id] = action.vulnerableVersions;

      return {
        ...state,
        vulnerableVersionsByComponent: newVulnerableVersionsByComponent,
        isFetchingVulnerableVersions: false,
      };
    }
    case actions.UPDATE_EXPLOIT_BEFORE_EDITING:
      return Object.assign({}, state, {
        exploitBeforeEdits: action.exploit,
      });
    case actions.UPDATE_SIGNATURE_SECTION:
      return Object.assign({}, state, {
        signatureSection: action.section,
      });
    case actions.UPDATE_COMPONENT_PACKAGE_MANAGER: {
      const field = !action.section
        ? 'componentPackageManager'
        : `${action.section}ComponentPackageManager`;
      return Object.assign({}, state, {
        [field]: action.manager,
      });
    }
    case actions.UPDATE_FIX_SECTION:
      return Object.assign({}, state, {
        fixSection: action.section,
      });
    case actions.UPDATE_EXPLOIT_ID:
      return Object.assign({}, state, {
        exploitId: action.id,
      });
    case actions.UPDATE_EXPLOIT_SECTION:
      return Object.assign({}, state, {
        exploitSection: action.section,
      });
    case actions.UPDATE_VULNERABLE_METHODS:
      return Object.assign({}, state, {
        vulnerableMethods: action.vulnerableMethods,
      });
    case actions.UPDATE_COMPONENT_VERSION:
      return Object.assign({}, state, {
        componentVersion: action.version,
      });
    case actions.UPDATE_VULNERABLE_VERSION: {
      return {
        ...state,
        activeVulnerableVersion: action.version,
      };
    }
    case actions.UPDATE_VULNERABLE_RANGE_BY_COMPONENT: {
      const newRangeByComponent = Object.assign({}, state.vulnerableRangeByComponent);
      const componentId = action.componentId ? action.componentId : state.componentId;

      newRangeByComponent[componentId] = action.range;
      return Object.assign({}, state, {
        vulnerableRangeByComponent: newRangeByComponent,
      });
    }
    case actions.UPDATE_IS_VALIDATING:
      return Object.assign({}, state, {
        isValidating: action.bool,
      });
    case actions.UPDATE_IS_ANALYZING:
      return Object.assign({}, state, {
        isAnalyzing: action.bool,
      });
    case actions.UPDATE_ANALYZING_ERROR:
      return Object.assign({}, state, {
        analyzingError: action.error,
      });
    case actions.UPDATE_VALIDATING_ERROR:
      return Object.assign({}, state, {
        validatingError: action.error,
      });
    case actions.UPDATE_VULNERABLE_METHOD_TO_ANALYZE:
      return Object.assign({}, state, {
        vulnerableMethodToAnalyze: action.method,
      });
    case actions.UPDATE_VALIDATED_METHOD:
      return Object.assign({}, state, {
        validatedMethod: action.method,
      });
    case actions.UPDATE_IS_APPLYING:
      return Object.assign({}, state, {
        isApplying: action.bool,
      });
    case actions.UPDATE_RANGE_SELECT_FOCUS:
      return Object.assign({}, state, {
        isRangeSelectFocused: action.bool,
      });
    case actions.POPULATE_REFERENCE_FILTERS:
      return Object.assign({}, state, {
        referenceFilters: action.referenceFilters,
      });
    case actions.TOGGLE_REFERENCE_FILTER: {
      const filters = Object.assign({}, state.referenceFilters);
      const currentValue = filters[action.filter];

      filters[action.filter] = !currentValue;

      return Object.assign({}, state, {
        referenceFilters: filters,
      });
    }
    case actions.TOGGLE_REFERENCE_DROPDOWN: {
      const current = state.isReferenceDropdownActive;
      return Object.assign({}, state, {
        isReferenceDropdownActive: !current,
      });
    }
    case actions.UPDATE_IS_SUBMITTING_ARTIFACT:
      return Object.assign({}, state, {
        isSubmittingArtifact: action.bool,
      });
    case actions.RECEIVE_VULN_METHOD_ANALYSIS:
      return Object.assign({}, state, {
        vulnMethodAnalysis: action.vulnMethodAnalysisData,
      });
    case actions.UPDATE_RECENT_VULNS:
      return Object.assign({}, state, {
        recentVulns: action.searchResults,
      });
    case actions.REQUEST_SAFE_VERSIONS_AFTER_RANGE:
      return Object.assign({}, state, {
        isFetchingSafeVersionsAfterRange: true,
      });
    case actions.RECEIVE_SAFE_VERSIONS_AFTER_RANGE:
      return Object.assign({}, state, {
        isFetchingSafeVersionsAfterRange: false,
        safeVersionsAfterRange: action.versions,
      });
    case actions.RESET_SAFE_VERSIONS_AFTER_RANGE:
      return Object.assign({}, state, {
        isFetchingSafeVersionsAfterRange: false,
        safeVersionsAfterRange: [],
      });
    case actions.UPDATE_ACTIVE_SAFE_VERSION:
      return Object.assign({}, state, {
        activeSafeVersion: action.version,
      });
    case actions.REQUEST_EXPLOITS:
      return Object.assign({}, state, {
        isFetchingExploits: true,
      });
    case actions.RECEIVE_EXPLOITS_SUCCESS:
      return Object.assign({}, state, {
        isFetchingExploits: false,
      });
    case actions.RECEIVE_EXPLOITS_ERROR:
      return Object.assign({}, state, {
        isFetchingExploits: false,
      });
    case actions.UPDATE_FE_VALIDATION: {
      const newfrontEndValidation = _.cloneDeep(state.frontEndValidation);
      newfrontEndValidation[action.field] = action.valid;

      return Object.assign({}, state, {
        frontEndValidation: newfrontEndValidation,
      });
    }
    case actions.UPDATE_EXPLOT_FILE_URL:
      return Object.assign({}, state, {
        currentExploitDownloadFileUrl: action.fileUrl,
      });
    case actions.GET_ARTIFACT:
      return Object.assign({}, state, {
        artifact: action.artifact,
      });
    case actions.FETCH_COMMIT_HASH_FIX_INFO_REQUEST:
      return Object.assign({}, state, {
        fixInfoByComponentAndCommitHash: {
          ...state.fixInfoByComponentAndCommitHash,
          isFetching: true,
          errorMessage: null,
        },
      });
    case actions.FETCH_COMMIT_HASH_FIX_INFO_SUCCESS:
      return Object.assign({}, state, {
        fixInfoByComponentAndCommitHash: {
          ...state.fixInfoByComponentAndCommitHash,
          isFetching: false,
          errorMessage: null,
          [action.componentId]: {
            ...state.fixInfoByComponentAndCommitHash[action.componentId],
            [action.commitHash]: action.fixInfo,
          },
        },
      });
    case actions.FETCH_COMMIT_HASH_FIX_INFO_ERROR: {
      return Object.assign({}, state, {
        fixInfoByComponentAndCommitHash: {
          ...state.fixInfoByComponentAndCommitHash,
          isFetching: false,
          errorMessage: action.errorMessage,
        },
      });
    }
    case actions.CHECK_COMPONENT_HEAD_SAFE_REQUEST:
      return Object.assign({}, state, {
        componentHeadSafeState: {
          ...state.componentHeadSafe,
          isFetching: true,
          errorMessage: null,
        },
      });
    case actions.CHECK_COMPONENT_HEAD_SAFE_FAILURE:
      return Object.assign({}, state, {
        componentHeadSafeState: {
          ...state.componentHeadSafe,
          isFetching: false,
          errorMessage: action.message,
        },
      });
    case actions.CHECK_COMPONENT_HEAD_SAFE_SUCCESS:
      return Object.assign({}, state, {
        componentHeadSafeState: {
          ...state.componentHeadSafe,
          isFetching: false,
          errorMessage: null,
          [action.componentId]: action.res,
        },
      });
    case actions.UPDATE_ARTIFACT_EXPLOITABILITY:
      return Object.assign({}, state, {
        exploitability: {...action.exploitability},
      });
    default:
      return state;
  }
};

export default artifactState;
